import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import LazyImage from '@/components/LazyImage';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import AIConsultantChat from '@/components/AIConsultantChat';
import { Link } from 'react-router-dom';

// Updated image array with new images
const images = [{
  id: 1,
  src: "/lovable-uploads/65bb54ca-e0a1-41d1-a6e7-e2b7e7b4fdd8.png",
  alt: "FixPadi AI-Powered Solutions"
}, {
  id: 2,
  src: "/lovable-uploads/6c1c889a-c147-4da5-b83e-e75df8b87850.png",
  alt: "Subscription Management Platform"
}, {
  id: 3,
  src: "/lovable-uploads/71eed97a-8a63-4270-be03-ff7be4850e84.png",
  alt: "Equine Edge Farm Management"
}, {
  id: 4,
  src: "/lovable-uploads/ff1e65ca-e0f8-4073-ae9f-12e9aab45184.png",
  alt: "MentorsWave AI Platform"
}, {
  id: 5,
  src: "/lovable-uploads/83c90c97-1151-4c23-ada1-9ec4bde8da51.png",
  alt: "VoiceScheduler AI Assistant"
}];
const HorizontalImageSlider = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // For automatic scrolling control
  const [autoScroll, setAutoScroll] = useState(true);
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollPosition(scrollRef.current?.scrollLeft || 0);
    setAutoScroll(false); // Disable auto scroll when user interacts
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (scrollRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 2; // Scroll speed multiplier
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollPosition - walk;
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setAutoScroll(true), 5000); // Re-enable auto scroll after some time
  };
  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
    }
  };
  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  };

  // Auto-scroll effect
  useEffect(() => {
    if (!autoScroll) return;
    const interval = setInterval(() => {
      if (scrollRef.current) {
        // If we've scrolled to the end, reset to beginning
        if (scrollRef.current.scrollLeft >= scrollRef.current.scrollWidth - scrollRef.current.clientWidth - 10) {
          scrollRef.current.scrollLeft = 0;
        } else {
          scrollRef.current.scrollBy({
            left: 300,
            behavior: 'smooth'
          });
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [autoScroll]);
  return <section className="py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black/90" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.1)_0%,_transparent_70%)]" />
      
      <div className="container relative z-10 mx-auto px-4 max-w-6xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-gold via-gold-light to-gold bg-clip-text text-transparent">Over 50+ MPV and Internal Tools Built and Launched in few weeks</span>
          </h2>
          
        </motion.div>
        
        {/* Navigation Controls - Desktop */}
        <div className="hidden md:flex justify-end mb-8 space-x-4">
          <button onClick={handleScrollLeft} className="w-12 h-12 rounded-full flex items-center justify-center bg-luxury-800/50 border border-gold/30 hover:bg-luxury-700 transition-colors" aria-label="Scroll left">
            <ChevronLeft className="h-6 w-6 text-gold" />
          </button>
          <button onClick={handleScrollRight} className="w-12 h-12 rounded-full flex items-center justify-center bg-luxury-800/50 border border-gold/30 hover:bg-luxury-700 transition-colors" aria-label="Scroll right">
            <ChevronRight className="h-6 w-6 text-gold" />
          </button>
        </div>
        
        {/* Horizontal Slider Container */}
        <div ref={scrollRef} className={cn("flex space-x-6 overflow-x-auto hide-scrollbar pb-8", isDragging ? "cursor-grabbing" : "cursor-grab")} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onMouseMove={handleMouseMove} style={{
        scrollBehavior: 'smooth',
        WebkitOverflowScrolling: 'touch'
      }}>
          {images.map(image => <motion.div key={image.id} whileHover={{
          y: -10,
          transition: {
            duration: 0.3
          }
        }} className="flex-shrink-0 w-[320px] sm:w-[400px] md:w-[500px] lg:w-[600px]">
              <div className="relative rounded-xl overflow-hidden bg-luxury-800/20 border border-white/10 shadow-lg">
                <LazyImage src={image.src} alt={image.alt} className="w-full h-auto object-contain transition-transform duration-700 hover:scale-105" aspectRatio="aspect-[16/9]" loadingStrategy="eager" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-50" />
                <div className="absolute bottom-0 left-0 w-full p-4">
                  <p className="text-white text-sm font-medium opacity-90">{image.alt}</p>
                </div>
              </div>
            </motion.div>)}
        </div>
        
        {/* Mobile Navigation Controls */}
        <div className="flex md:hidden justify-center mt-6 space-x-4">
          <button onClick={handleScrollLeft} className="w-10 h-10 rounded-full flex items-center justify-center bg-luxury-800/50 border border-gold/30" aria-label="Scroll left">
            <ChevronLeft className="h-5 w-5 text-gold" />
          </button>
          <button onClick={handleScrollRight} className="w-10 h-10 rounded-full flex items-center justify-center bg-luxury-800/50 border border-gold/30" aria-label="Scroll right">
            <ChevronRight className="h-5 w-5 text-gold" />
          </button>
        </div>

        {/* Get Started Button with AI Consultant Modal */}
        <div className="text-center mt-12">
          <Dialog>
            <DialogTrigger asChild>
              <Button 
                size="lg" 
                className="bg-gold hover:bg-gold/90 text-black font-semibold px-8 py-3"
              >
                What can we build for you?
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl h-[80vh] p-0">
              <AIConsultantChat fullPage={true} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Add custom CSS for hiding scrollbar */}
        <style>{`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </div>
    </section>;
};
export default HorizontalImageSlider;
